import { z } from 'zod';
/**
 * The Allocation automations an admin can see, toggle, and inspect.
 *
 * This list is the only registry. Adding a key here makes an automation
 * *visible* and *toggleable*; it does not make it runnable and it does not make
 * it enabled. A newly added key has no row in `automation_flags`, and a missing
 * row reads as disabled — so a fifth automation arrives OFF without anyone
 * having to remember to turn it off. See `allocation-automation-control.ts` in
 * the API for the resolution order.
 */
export const ALLOCATION_AUTOMATION_KEYS = [
    'issued_nods',
    'confirmed_terminations',
    'training_routing',
    'approved_resignations',
    'training_withdrawal',
    'cancellation_events',
    'bench_board_projection',
    'training_episode_reconciliation',
    'unmatching_soon',
    'outgoing_release',
    'rematch_turnover',
    'preventive_suspension_note',
    'founder_nonpayment',
    'resignation_retraction',
    'cancellation_retraction',
    'rematch_request_notice',
    'replacement_updates',
    'leave_return',
    'pending_deliberation',
    'cancelled_matching',
];
export const allocationAutomationKeySchema = z.enum(ALLOCATION_AUTOMATION_KEYS);
/**
 * Whether a run PLANS or WRITES.
 *
 * Declared up here, ahead of everything that uses it, because it is no longer
 * only a property of a recorded run: it is now STORED per automation on
 * `automation_flags.run_mode` and reported by the list endpoint below.
 */
export const allocationAutomationRunModeSchema = z.enum(['preview', 'apply']);
/**
 * One automation as the admin screen sees it.
 *
 * There is exactly ONE thing that decides whether an automation runs: its
 * `automation_flags` row. No environment variable narrows it, in any
 * environment. So this object carries no `governor`, no `deployGateOpen` and no
 * `toggleable` — every one of those existed only to explain a deploy-time gate
 * that no longer exists, and a field that can only ever hold one value is an
 * affordance that lies.
 *
 * `enabled` and `runtimeFlag` are therefore the same decision seen twice, and
 * both are kept on purpose:
 *
 * - `enabled` is the answer to "would a request be served right now".
 * - `runtimeFlag` is the stored row, and its THIRD state matters: `null` means
 *   no row has ever been written, which is off but is not the same event as
 *   somebody deliberately switching it off. The screen says which.
 */
export const allocationAutomationStateSchema = z.object({
    key: allocationAutomationKeySchema,
    name: z.string(),
    description: z.string(),
    /** The routes this automation is served by, for the operator's orientation. */
    routes: z.array(z.string()),
    /** Would a request be served right now. Equivalent to `runtimeFlag === true`. */
    enabled: z.boolean(),
    /**
     * The stored flag. `null` means no row exists — never configured, and off.
     *
     * Anything other than a stored `true` is off: a missing row, an unreadable
     * table, a malformed value. That is now the only thing between a fresh
     * deployment and a running automation.
     */
    runtimeFlag: z.boolean().nullable(),
    updatedAt: z.string().nullable(),
    updatedByEmail: z.string().nullable(),
    /**
     * Whether the hourly dispatcher will start this automation unattended.
     *
     * The STORED column, reported faithfully — not `enabled && schedule_enabled`.
     * The two PUT routes maintain the invariant that a schedule is never armed on
     * a disabled automation, but `automation_flags` is also reachable by hand from
     * the Allocation repository's SQL console, so a row can exist that the
     * invariant forbids. Collapsing the two here would hide exactly that row from
     * the only screen that could show somebody it is wrong.
     *
     * There is no third `null` state, unlike `runtimeFlag`. A missing row is off
     * and unarmed, and "nobody has ever decided about the schedule" is not a
     * distinction the schedule switch words differently.
     */
    scheduleEnabled: z.boolean(),
    /** When the schedule was last armed or disarmed. `null` if it never was. */
    scheduleUpdatedAt: z.string().nullable(),
    /**
     * Who armed or disarmed the schedule.
     *
     * Separate from `updatedByEmail` on purpose: arming an UNATTENDED run is a
     * different decision from allowing a run at all, often made by a different
     * person weeks apart, and one field would let the later write erase the
     * account of the earlier one.
     */
    scheduleUpdatedByEmail: z.string().nullable(),
    /**
     * Whether a run of this automation PLANS or WRITES — for BOTH triggers.
     *
     * ## A reversal, recorded rather than quietly performed
     *
     * The Run now button and the hourly dispatcher were both preview-only
     * STRUCTURALLY: each held its mode in a module constant, and the argument for
     * that — a default is a value somebody can supply, a constant is not — was
     * made here and won. The product owner has since decided, with the
     * consequence stated to them in those words, that an operator must be able to
     * arm a real write and that the hourly job must honour it. So the constants
     * are gone, and what they guaranteed is gone with them: an automation that is
     * enabled, schedule-armed and in `apply` writes to real doer records every
     * hour with nobody present.
     *
     * What replaced them is NOT a request field. The mode is STORED, set in one
     * deliberate, audited act through `PUT /v1/allocation/automations/:key/run-mode`,
     * and neither trigger's request body can carry it: the Run now body still
     * accepts nothing but `reason`, and the dispatcher's is still `.strict()` and
     * empty. That property — nobody chooses the mode on the request that runs — is
     * the one thing kept from the design being reversed, and it is why a stored
     * column was chosen over a body field.
     *
     * ## What now carries the safety
     *
     * Four conditions, ANDed. None substitutes for another:
     *
     *  1. `enabled` — may it run at all;
     *  2. this mode, which defaults to `preview` in the migration and is cleared
     *     back to `preview` in the same write that switches the automation off, so
     *     that "off" never hides armed state which silently returns;
     *  3. the automation's deploy-time apply gate (`applyGateEnv`), re-checked at
     *     the moment of EVERY run and never merely when the mode was set. A stored
     *     `apply` whose gate has since closed REFUSES the run. It deliberately does
     *     not quietly downgrade itself to a preview: a downgrade reports
     *     `succeeded` over a write that never happened, which is the one failure
     *     shape this family of code treats as worse than stopping;
     *  4. `training_routing`, which may never be set to `apply` at all, because its
     *     live path posts a real Keap enrolment form to a real external system.
     *
     * Reported as STORED, not as the effective answer, for exactly the reason
     * `scheduleEnabled` is: `automation_flags` is hand-edited from the Allocation
     * SQL console, and this screen is the only place anybody would see a row the
     * API's own routes could not have written.
     */
    runMode: allocationAutomationRunModeSchema,
    /**
     * When the mode was last set OR reset. `null` if it never was — so it is
     * `preview` and nobody has ever had an opinion about it.
     */
    runModeUpdatedAt: z.string().nullable(),
    /**
     * Who CHOSE the current mode — `null` when nobody did.
     *
     * Kept apart from `updatedByEmail` and `scheduleUpdatedByEmail` for the same
     * reason those two are kept apart from each other: "may it run", "does it run
     * unattended" and "does it write" are three decisions, often taken by
     * different people weeks apart, and one field would let the latest write erase
     * the account of the other two.
     *
     * ## `null` with a non-null `runModeUpdatedAt` means RESET, not chosen
     *
     * Switching an automation off stands its mode back down to `preview` in the
     * same write that disarms its schedule. The operator who did that switched the
     * automation OFF; they did not sit down and decide that preview was the right
     * mode for it. Writing their name here would put a decision they never made in
     * the one field somebody later reads to find out who made it.
     *
     * So a reset stamps the timestamp and clears the attribution. The account of
     * the act is not lost — it is on the toggle's own `updatedByEmail` and in the
     * `allocation.automation.flag_changed` audit row, both carrying the same
     * instant, and that audit row records `run_mode_reset: true` explicitly. The
     * screen should read this pair as "reset to preview when the automation was
     * switched off", not as "preview, chosen by nobody".
     */
    runModeUpdatedByEmail: z.string().nullable(),
    /*
     * `applyGateOpen` and `applyGatesMissing` used to live here, reporting the
     * per-automation `ALLOCATION_*_APPLY_ENABLED` variables.
     *
     * Those variables are gone by an explicit product decision — the switch
     * decides whether an automation RUNS and the mode decides whether it WRITES,
     * and a second deploy-time lock on the same door was redundant. The fields go
     * with them rather than being left reporting `true` and `[]` forever: a field
     * that can only ever say "open" keeps a control alive on the screen with
     * nothing behind it.
     *
     * One write variable did NOT go: `ALLOCATION_TRAINING_HANDOFF_MODE=deliver`,
     * still enforced on `training_routing`'s Keap handoff, which posts a real
     * enrolment form for a real person to a third party. It is deliberately not
     * reported here, because it is not something this screen can act on — that
     * automation already answers `applyModeSupported: false`, which is the field
     * a screen should render, and its live handoff is not reachable from the
     * screen in any mode. See `AUTOMATION_REGISTRY`.
     */
    /**
     * Whether `apply` is a mode this automation may EVER be set to.
     *
     * False for `training_routing` alone, and false regardless of environment: a
     * live run of it posts a Keap enrolment form for a real person to a real
     * external system, and no deploy variable unlocks that from this screen.
     *
     * It outlived `applyGateOpen`, which used to sit beside it and said "not
     * configured HERE". That distinction is why this field is worth keeping on its
     * own: "this one never applies" is a property of the executor that no deploy
     * changes, and only the other sentence invited somebody to go and configure
     * something.
     */
    applyModeSupported: z.boolean(),
}).strict();
export const allocationAutomationListResponseSchema = z.object({
    automations: z.array(allocationAutomationStateSchema),
    /**
     * Whether this environment permits ANY automation to write with nobody present
     * — `ALLOCATION_UNATTENDED_APPLY_ENABLED === 'true'`.
     *
     * ## Why it is here and not on each automation
     *
     * Because it is not a property of an automation. It is one deploy-time
     * variable covering the whole environment, and repeating it on every entry
     * would invite a screen to render it per row as though it could differ — which
     * is the shape of a control somebody then goes looking for per automation.
     *
     * It is now the only deploy-time write variable this response reports. The
     * per-automation `ALLOCATION_*_APPLY_ENABLED` gates it used to sit beside are
     * gone; this one survives because it answers a different question — not "may
     * this automation write" but "may anything write here with nobody watching".
     *
     * ## What it is for, stated as the defect it prevents
     *
     * Without it the screen cannot tell the truth in the most consequential dialog
     * it has. An operator arms the hourly schedule on an automation stored as
     * `apply`, reads a confirmation saying it will write to real doer records by
     * itself every hour — and then it previews every hour, because this gate is
     * shut. The screen would learn that retroactively, an hour later, from the
     * downgrade note on the first run. Gate-open-but-unattended-shut is a
     * configuration that is actually used: it is what a staging environment looks
     * like when an attended demo may write and the cron may not.
     *
     * It is advice, not the authority. The dispatcher re-reads the variable on
     * every tick and remains the authority.
     */
    unattendedApplyEnabled: z.boolean(),
    /**
     * Whether the demo estate reset would be permitted in this environment.
     *
     * True only when BOTH of its guards pass: not a production environment AND
     * `ALLOCATION_DEMO_RESET_ENABLED === 'true'`. Environment-wide like
     * `unattendedApplyEnabled`, and for the same reason — it is a property of the
     * deployment, not of any automation.
     *
     * ## Why a bare boolean, and not the `applyGatesMissing` treatment
     *
     * `applyGatesMissing` names its variables because an admin arming a write can
     * act on the name: the answer is a deploy, and telling them which one sends
     * them to it. Nobody can act on this one. The reset is not a control an
     * environment is expected to gain — production is meant to refuse it forever —
     * so naming the shut guard would publish deploy configuration to every admin
     * who loads the screen in exchange for nothing they could do with it.
     *
     * ## What it prevents
     *
     * A screen with no way to ask this hides the button on its own build-time
     * environment variable, which covers the production half of the answer and not
     * the gate half. In a non-production deployment with the gate unset, that
     * screen shows a button that 503s every single time it is pressed — and a
     * control that can never work is worse than no control, because the first
     * thing an operator does with it is report a bug.
     *
     * Advice, like the fields above. The reset route re-checks both guards itself
     * and remains the authority.
     */
    demoResetAvailable: z.boolean(),
}).strict();
export const allocationAutomationToggleRequestSchema = z.object({
    enabled: z.boolean(),
    /** Free-text justification, stored on the flag row for the next reader. */
    reason: z.string().trim().min(1).max(500),
}).strict();
/**
 * Arming or disarming the hourly schedule for one automation.
 *
 * `reason` is REQUIRED, and more firmly than on the toggle above rather than
 * less. The toggle decides whether a run is allowed; this decides whether one
 * happens with nobody watching, on a cadence, against employment records. The
 * account of who armed it and why is the only thing a reader of a 3am run row
 * has, because the run itself has no actor by construction.
 *
 * Deliberately NOT merged into `allocationAutomationToggleRequestSchema` with an
 * optional field. One request body that can mean either "may it run" or "does it
 * run by itself" is one typo away from an operator arming an unattended job
 * while believing they were only permitting a manual one.
 */
export const allocationAutomationScheduleRequestSchema = z.object({
    enabled: z.boolean(),
    reason: z.string().trim().min(1).max(500),
}).strict();
/**
 * Why arming a schedule was refused.
 *
 * One value today, and it is an enum anyway so the screen matches on a code
 * rather than on prose — the same rule the retry and manual-run refusals follow.
 *
 * - `automation_disabled` — the automation's `automation_flags` row is not
 *                           enabled. You cannot schedule something that is not
 *                           allowed to run, and the refusal is enforced in the
 *                           UPDATE itself rather than by a preceding read, so
 *                           there is no window in which a concurrent switch-off
 *                           can be overtaken by an arming write.
 */
export const allocationAutomationScheduleRefusalSchema = z.enum([
    'automation_disabled',
]);
/**
 * Setting whether an automation WRITES.
 *
 * A third body, alongside the toggle and the schedule, and deliberately not a
 * third optional field on either of them. The three answer different questions —
 * may it run, does it run unattended, does it write — and one body that can mean
 * any of them is one mis-keyed field away from an operator arming live writes
 * against employment records while believing they were permitting a preview.
 *
 * `reason` is REQUIRED IN BOTH DIRECTIONS, which the toggle and the schedule are
 * not symmetric about. Arming `apply` obviously earns an account. So does
 * standing one down: "why did this stop writing" is the question somebody asks
 * a fortnight later when they find a backlog nobody processed, and an unexplained
 * revert leaves them nothing to read. The row records only the latest act, so an
 * unjustified one erases the justified one it replaced.
 */
export const allocationAutomationRunModeRequestSchema = z.object({
    mode: allocationAutomationRunModeSchema,
    reason: z.string().trim().min(1).max(500),
}).strict();
/**
 * Why setting the mode was refused, as a code the screen can match on.
 *
 * - `automation_disabled` — the `automation_flags` row is not enabled. A mode on
 *                           an automation that may not run is a setting with no
 *                           subject, and storing one would leave armed state
 *                           waiting behind an off switch. Enforced in the UPDATE
 *                           itself, as the schedule arming is, so there is no
 *                           window in which a concurrent switch-off is overtaken
 *                           by an arming write.
 * - `apply_gate_closed`   — `apply` was asked for while the automation's
 *                           deploy-time apply variables are not satisfied here.
 *                           The response NAMES them in `gates`, because "this is
 *                           refused" without saying which variable is missing
 *                           sends an operator to a person rather than to a fix.
 *                           Note this is not only a set-time check: the same gates
 *                           are re-read at the moment of every run.
 * - `apply_unsupported`   — `training_routing`. Its live path posts a real Keap
 *                           enrolment form to a real external system, its executor
 *                           throws on apply by design, and no admin-screen switch
 *                           is allowed to reach that. Refused here so the state is
 *                           never stored, and refused again at run time so a row
 *                           hand-edited into the table still cannot run.
 */
export const allocationAutomationRunModeRefusalSchema = z.enum([
    'automation_disabled',
    'apply_gate_closed',
    'apply_unsupported',
]);
/* -------------------------------------------------------------------------- */
/* Run records                                                                */
/* -------------------------------------------------------------------------- */
export const allocationAutomationRunStatusSchema = z.enum([
    'running',
    'succeeded',
    'partial',
    'failed',
]);
/**
 * How the run was started.
 *
 * `retry` is its own value rather than a flag on `manual`, because a retry is
 * the only trigger that carries a link to an earlier run and a required reason.
 * Filtering the history to "which runs happened because a human pressed retry"
 * is a question an operator asks, and it should not require joining.
 */
export const allocationAutomationRunTriggerSchema = z.enum([
    'schedule',
    'manual',
    'webhook',
    'retry',
]);
export const allocationAutomationItemOutcomeSchema = z.enum([
    'changed',
    'no_change',
    'skipped',
    'blocked',
    'failed',
]);
/**
 * One field that moved, in the operator's words rather than the database's.
 *
 * `oldValue` / `newValue` are rendered strings, not typed column values: the
 * screen's job is "this went from A to B", and a caller that needs the typed
 * value has `entityId` and the table.
 *
 * `redacted` marks a field whose content is deliberately not stored — the
 * disciplinary narrative written into `doers.quick_notes` is the case. The HR
 * apply path already refuses to keep that text in process state (see the
 * `HrCaseClaim.noteReceivedOn` comment in `allocation-hr-case-apply.ts`), and a
 * change log that copied it would undo that decision in a new table.
 */
export const allocationAutomationChangeSchema = z.object({
    field: z.string(),
    label: z.string(),
    oldValue: z.string().nullable(),
    newValue: z.string().nullable(),
    redacted: z.boolean(),
}).strict();
export const allocationAutomationRunItemSchema = z.object({
    id: z.string().uuid(),
    entityTable: z.string(),
    entityId: z.string().uuid().nullable(),
    /**
     * Who the record is about. A doer row reads `First Last (CODE)`; a doer the
     * source named and no Allocation doer matches reads "Doer not found for
     * employee ID …"; a request row names its request.
     */
    entityLabel: z.string().nullable(),
    caseRef: z.string().nullable(),
    /** The route's own per-case `decision`, carried through verbatim. */
    decision: z.string(),
    outcome: allocationAutomationItemOutcomeSchema,
    reason: z.string().nullable(),
    /** The route's own `findings`, carried through verbatim. */
    findings: z.array(z.object({
        code: z.string(),
        detail: z.string(),
    }).strict()),
    changes: z.array(allocationAutomationChangeSchema),
    createdAt: z.string(),
    /**
     * Whether pressing "retry" on this item could do anything.
     *
     * Decided by the API, not the screen. The rule depends on the automation, the
     * run's mode and the item's outcome, and a UI that re-derived it would drift
     * from the route that enforces it — so the route computes it once and the
     * screen renders it. A `false` here always comes with a reason.
     */
    retryable: z.boolean(),
    /** Why a retry is refused, in an operator's words. `null` when retryable. */
    retryBlockedReason: z.string().nullable(),
    /**
     * The doer to open from this item, for LINKING ONLY: the one doer the API
     * identified where `entityId` does not name that doer (a rematch request, an
     * event-ledger row). Never a write target. Sent only when there is one, and
     * optional in the Allocation screen's strict schema, which must accept it
     * before the API sends it.
     */
    linkDoerId: z.string().uuid().nullable().optional(),
}).strict();
export const allocationAutomationRunSchema = z.object({
    id: z.string().uuid(),
    automationKey: allocationAutomationKeySchema,
    mode: allocationAutomationRunModeSchema,
    trigger: allocationAutomationRunTriggerSchema,
    status: allocationAutomationRunStatusSchema,
    actorEmail: z.string().nullable(),
    startedAt: z.string(),
    finishedAt: z.string().nullable(),
    itemsConsidered: z.number().int().min(0),
    itemsChanged: z.number().int().min(0),
    itemsSkipped: z.number().int().min(0),
    itemsFailed: z.number().int().min(0),
    error: z.string().nullable(),
    /**
     * The free text a human gave for starting this run — plus one machine-written
     * exception.
     *
     * A retry has one, and so does an applying manual run. A scheduled run
     * genuinely has no reason to state, EXCEPT when it was downgraded: that run
     * carries the system's own sentence naming the environment variables that held
     * the write back, because somebody opening a run whose `mode` is `preview` on
     * an automation the screen shows as `apply` has nowhere else to find out why.
     *
     * Do NOT detect a downgrade from this field. `downgradedFrom` below is the
     * machine-readable answer; this is the prose for a human.
     */
    reason: z.string().nullable(),
    /**
     * The mode this run was ARMED for but did not use. `null` for every ordinary
     * run.
     *
     * `'apply'` means: the automation's stored `run_mode` was `apply`, an apply gate
     * was shut, and the hourly dispatcher ran it as a preview instead of skipping
     * it. `mode` therefore reads `preview` and this says what that preview was
     * supposed to be.
     *
     * ## Why a column rather than an inference
     *
     * The screen's first version detected this as
     * `mode === 'preview' && trigger === 'schedule' && reason !== null`. That
     * happened to be correct, because nothing else writes a reason on an unattended
     * run — which is true today, is a coincidence of two unrelated rules, and is not
     * a property anybody will remember to preserve. The first scheduled run that
     * legitimately carries a reason would silently relabel itself a held-back
     * write.
     *
     * Stored as the mode rather than as the outcome code
     * (`applied_downgraded_to_preview`) because the run record answers "what was
     * this run", not "what did one dispatch tick report". The pair
     * `mode: 'preview', downgradedFrom: 'apply'` is readable on its own, survives
     * a rename of the dispatch code, and generalises if a third mode is ever added.
     */
    downgradedFrom: allocationAutomationRunModeSchema.nullable(),
    /** The run this one retries, and the single item within it. */
    retryOfRunId: z.string().uuid().nullable(),
    retryOfItemId: z.string().uuid().nullable(),
}).strict();
/**
 * Server-side filters for the run history.
 *
 * Deliberately server-side. The table grows by one row per automation per
 * scheduled run forever, so a client-side filter over a fetched page would show
 * "the matches within the most recent 25 runs" while looking exactly like "the
 * matches" — which is a lie that gets worse every day.
 *
 * `startedAfter` / `startedBefore` are full ISO instants rather than dates, so
 * the caller decides the timezone the day boundary falls on rather than the
 * server guessing. `startedBefore` is INCLUSIVE, so a UI sending the end of a
 * chosen day includes that day.
 */
export const allocationAutomationRunListQuerySchema = z.object({
    automationKey: allocationAutomationKeySchema.optional(),
    mode: allocationAutomationRunModeSchema.optional(),
    status: allocationAutomationRunStatusSchema.optional(),
    trigger: allocationAutomationRunTriggerSchema.optional(),
    startedAfter: z.string().datetime({ offset: true }).optional(),
    startedBefore: z.string().datetime({ offset: true }).optional(),
    offset: z.coerce.number().int().min(0).default(0),
    limit: z.coerce.number().int().min(1).max(100).default(25),
}).strict();
export const allocationAutomationRunListResponseSchema = z.object({
    runs: z.array(allocationAutomationRunSchema),
    hasMore: z.boolean(),
    /**
     * How many runs match the filter in total, ignoring the page.
     *
     * `null` when the database did not return a count. A paging control that
     * silently rendered a missing count as zero would say "no runs" over a page
     * that plainly has some, so the absence is representable.
     */
    total: z.number().int().min(0).nullable(),
}).strict();
export const allocationAutomationRunDetailResponseSchema = z.object({
    run: allocationAutomationRunSchema,
    items: z.array(allocationAutomationRunItemSchema),
    /**
     * True when this run has more items than the page returned.
     *
     * Real runs reach 22,949 items against a 500-item page, so this is the normal
     * case rather than an edge one, and a screen that ignores it shows 500 records
     * as if they were all of them.
     */
    hasMore: z.boolean(),
    /**
     * True when the per-field before/after could not be read for this page.
     *
     * The items are still returned — they are the half that matters — but every
     * `changes` array is empty for a reason that is NOT "nothing moved". A client
     * must say so rather than render an absence as a fact.
     */
    changesUnavailable: z.boolean(),
    /**
     * Records this run considered and deliberately did NOT list, because the
     * automation's own evidence showed they were already done — a claim settled
     * by an earlier run, a handoff already delivered, an endorsement inside the
     * cutover baseline.
     *
     * Derived as `run.itemsConsidered` minus the run's item rows, and only for a
     * `succeeded` run: for any other status, or when the database returned no
     * count, it is `null` ("not known"), never a guess. A screen shows it as a
     * plain sentence so a short list is not read as the whole run.
     *
     * Optional so a response from an API that predates it still parses.
     */
    itemsNotListed: z.number().int().min(0).nullable().optional(),
}).strict();
/* -------------------------------------------------------------------------- */
/* Retry                                                                      */
/* -------------------------------------------------------------------------- */
/**
 * Retrying ONE item of a past run.
 *
 * The unit is the item, not the run. These automations read a live source, so
 * re-running one whole run would also pick up every record that has appeared
 * since — which is a new run wearing the word "retry". Scoping to the item an
 * operator is actually looking at keeps the blast radius to the record they
 * chose. See `docs/allocation-automation-control.md` for the full reasoning and
 * for what a retry may and may not do.
 *
 * `reason` is required for the same accountability as the toggle: a retry is a
 * write, and the next person to read the history should not have to guess why
 * somebody re-ran a job against an employment record.
 */
export const allocationAutomationRunRetryRequestSchema = z.object({
    reason: z.string().trim().min(1).max(500),
}).strict();
/**
 * Why a retry was refused before it ran anything.
 *
 * Carried as a code as well as a message so the screen can react without
 * matching on prose:
 *
 * - `not_retryable`    — the item's outcome is not one a replay can change. A
 *                        `blocked` item is the case: a human decision has not
 *                        arrived, so retrying it simply blocks again.
 * - `runtime_flag_off` — the automation is switched OFF right now. Re-read at
 *                        the moment of the retry, never inherited from when the
 *                        original run happened, and it also covers the case
 *                        where the flag cannot be read at all — an unanswerable
 *                        question is a refusal, not a permission.
 * - `apply_not_armed`  — the automation's separate apply variable is not set, so
 *                        an apply retry would be a write the environment forbids.
 *                        This is the read-versus-write axis and it is deliberately
 *                        NOT collapsed into the switch.
 * - `unsupported`      — this automation has no retry path; the message says what
 *                        to use instead.
 */
export const allocationAutomationRetryRefusalSchema = z.enum([
    'not_retryable',
    'runtime_flag_off',
    'apply_not_armed',
    'unsupported',
]);
/** The new run a retry created, returned so the screen can open it at once. */
export const allocationAutomationRunRetryResponseSchema = z.object({
    run: allocationAutomationRunSchema,
    items: z.array(allocationAutomationRunItemSchema),
}).strict();
/* -------------------------------------------------------------------------- */
/* The structured per-run log                                                 */
/* -------------------------------------------------------------------------- */
/**
 * One line of the ordered story of a run.
 *
 * This is NOT Cloud Run stdout. It is derived entirely from what the run record
 * already stores — the run row, its items, their findings and their changes —
 * so it needs no new dependency, no new IAM grant and no log retention window,
 * and it survives as long as the run does. See
 * `docs/allocation-automation-control.md` for why raw Cloud Logging is a
 * separate decision rather than this one.
 */
export const allocationAutomationRunLogLevelSchema = z.enum(['info', 'warning', 'error']);
export const allocationAutomationRunLogEntrySchema = z.object({
    /** Stable within a run, for React keys and for linking. */
    id: z.string(),
    at: z.string(),
    level: allocationAutomationRunLogLevelSchema,
    /** A machine code — `run_started`, `item_failed`, `change`, `run_finished`. */
    event: z.string(),
    message: z.string(),
    /** The run item this line belongs to, when it belongs to one. */
    itemId: z.string().uuid().nullable(),
    /**
     * Which record this line belongs to, on EVERY line of that record — its item
     * line, each change line and each finding line alike. The doer CODE, never a
     * name; the item's case reference when the run never resolved it to a doer,
     * with the three machine event keys (bench board, cancellation episode,
     * training handoff) shown readably rather than raw. `null` on run-level lines, and on a record line only when the run recorded
     * no identifier of either kind. Decided once, by `recordIdentifier` in
     * `lib/allocation-automation-run-log.ts`.
     */
    entityLabel: z.string().nullable(),
}).strict();
export const allocationAutomationRunLogResponseSchema = z.object({
    runId: z.string().uuid(),
    entries: z.array(allocationAutomationRunLogEntrySchema),
    /**
     * True when the run's item detail is known to be incomplete — a `partial`
     * run, or a page cap. A log that quietly stopped short would be read as "and
     * then nothing else happened".
     */
    truncated: z.boolean(),
}).strict();
/* -------------------------------------------------------------------------- */
/* Starting a run by hand                                                     */
/* -------------------------------------------------------------------------- */
/**
 * Starting a whole automation from the admin screen.
 *
 * ## Why this exists at all
 *
 * Nothing invokes the three internal automation endpoints. There is no Cloud
 * Scheduler job for `process-cases`, `process-bench` or `process-offboarding`,
 * in staging or in production — every reference to them in the repository is a
 * route definition, the registry, the gate middleware, a test or a document. So
 * until this endpoint, an automation ran only when a person POSTed to an
 * internal path with the internal API key, and switching one ON in the admin
 * screen caused nothing whatsoever to happen. The screen listed runs it had no
 * way to produce.
 *
 * ## There is still NO `mode` field, and that has survived the reversal
 *
 * A manual run used to be a preview because a module constant said so. It is now
 * a preview or an apply because the automation's STORED `run_mode` says so. What
 * did not change is that no field of this body reaches the mode: the schema is
 * still `.strict()` and still carries nothing but `reason`, so `{ mode: 'apply' }`
 * is a 400 rather than an instruction. The mode is set once, in its own audited
 * request, by somebody who had to name the automation and give a reason — never
 * smuggled in on the request that runs.
 *
 * ## `reason` is OPTIONAL for a preview and REQUIRED for an apply
 *
 * The previous note here promised, in as many words, that "if this endpoint ever
 * gains an apply mode, `reason` becomes required in the same breath". It has, so
 * it is — but only when the stored mode is `apply`, which is why the requirement
 * cannot live in this schema and is enforced by the route instead (400
 * `reason_required`).
 *
 * The asymmetry is the point. A preview changes nothing, and demanding a
 * justification for one trains operators to type "x" into a box, which is how a
 * required reason stops meaning anything on the requests where it matters. An
 * apply writes to employment records, and `automation_runs.reason` is the only
 * per-run account of who asked for that and why — the arming audit row says the
 * mode was set, not that this particular sweep was wanted.
 *
 * A client can tell which it will be before it asks: `runMode` is on every
 * automation in the list response.
 */
export const allocationAutomationRunStartRequestSchema = z.object({
    /**
     * A short operator note, recorded on the run for the next reader. Optional
     * for a preview; the route requires it when the stored mode is `apply`.
     */
    reason: z.string().trim().min(1).max(500).optional(),
}).strict();
/**
 * Why a manual run was refused before anything was executed.
 *
 * A code as well as a message, for the same reason the retry refusals carry one:
 * the screen must be able to react without matching on prose.
 *
 * - `automation_disabled` — the automation's `automation_flags` row is not
 *                           enabled. Checked in the run route itself, not
 *                           inherited from the gate middleware: that middleware
 *                           guards the INTERNAL paths, and this is a new admin
 *                           path that must not become a way around the switch.
 * - `run_in_progress`     — a run of this automation is already `running`. Two
 *                           clicks must not become two runs over the same live
 *                           source.
 * - `unsupported`         — this automation has no unscoped executor. Not
 *                           reachable today: every registered automation has
 *                           one. The message says why when it is.
 * - `apply_unsupported`   — the stored mode is `apply` on `training_routing`,
 *                           whose live path posts a real Keap enrolment form. The
 *                           run-mode route refuses to store that, so reaching this
 *                           means the row was hand-edited — which is precisely why
 *                           the run path checks it again rather than trusting the
 *                           write path to have been the only writer.
 * - `apply_gate_closed`   — the stored mode is `apply` but the automation's
 *                           deploy-time apply variables are not satisfied HERE,
 *                           NOW. The response names them in `gates`. A stored
 *                           `apply` never outranks a deploy-time gate, and the run
 *                           refuses rather than silently running as a preview and
 *                           reporting success over a write that did not happen.
 * - `reason_required`     — the stored mode is `apply` and no reason was given.
 *                           An applying run's `reason` is the only per-run account
 *                           of who wanted this sweep.
 * - `demo_reset_in_progress` — "Reset demo state" holds its lease; a run now
 *                           would read a half-reset estate. Kept apart from
 *                           `run_in_progress`, which the screen words as "a run
 *                           of this automation is in the history".
 */
export const allocationAutomationRunStartRefusalSchema = z.enum([
    'automation_disabled',
    'run_in_progress',
    'unsupported',
    'apply_unsupported',
    'apply_gate_closed',
    'reason_required',
    // "Reset demo state" holds its lease, so the estate a run would read is
    // half rewritten. Nothing was started. Staging only in practice.
    'demo_reset_in_progress',
]);
/**
 * The run a manual start produced.
 *
 * The run row ONLY — deliberately not the items, unlike the retry response. A
 * retry produces one item by construction; a full run of `issued_nods` produced
 * 22,949, and serialising those into the response of the button that started
 * them would be a multi-megabyte body nobody asked for. The screen already has
 * a paged detail route, and this response carries the id that opens it.
 */
export const allocationAutomationRunStartResponseSchema = z.object({
    run: allocationAutomationRunSchema,
}).strict();
/* -------------------------------------------------------------------------- */
/* The hourly scheduled dispatch                                              */
/* -------------------------------------------------------------------------- */
/**
 * What happened to one automation during one scheduled dispatch.
 *
 * `skipped` is not a failure and `failed` is not a refusal, and the difference
 * is the whole reason this is three values rather than a boolean: an operator
 * looking at a quiet hour needs to know whether the automation declined to run,
 * was never armed, or ran and threw.
 */
export const allocationAutomationScheduledOutcomeStatusSchema = z.enum([
    'ran',
    'skipped',
    'failed',
]);
/**
 * Why an automation did not simply run, as a code the caller can branch on.
 *
 * - `automation_disabled` — its `automation_flags` row is not enabled. The
 *                           schedule is irrelevant; this is checked first.
 * - `schedule_disabled`   — allowed to run, but not armed for the schedule.
 *                           This is the ordinary state and the default one.
 * - `run_in_progress`     — a run of this automation is still `running`. These
 *                           read a LIVE source, so two overlapping runs are two
 *                           reads of the same tabs racing to record
 *                           contradictory items. Skipped, never stacked.
 * - `unsupported`         — in the registry with no run implementation. Not
 *                           reachable today: every registered automation has
 *                           one.
 * - `budget_exhausted`    — the dispatch ran out of its wall-clock budget before
 *                           reaching this one. It is NOT an error: the next
 *                           hourly tick starts with the automations that waited
 *                           longest. See the dispatcher for the arithmetic.
 * - `not_recorded`        — the run row could not be written, so the run was not
 *                           started. A scheduled run is a PREVIEW: its record is
 *                           the entire product, so running one that cannot be
 *                           recorded is cost with no output.
 * - `executor_failed`     — it ran and threw. `message` carries the error, and
 *                           the run row is finished as `failed`.
 */
/**
 * Two of these are about the stored MODE rather than the switches, and they are
 * deliberately not the same shape as each other.
 *
 * - `applied_downgraded_to_preview` is the only code that accompanies
 *   `status: 'ran'`. The automation IS armed to apply, but this environment does
 *   not permit an unattended writer — either `ALLOCATION_UNATTENDED_APPLY_ENABLED`
 *   is not set or the automation's own apply variable is not — so the tick ran it
 *   as a PREVIEW. It is reported rather than skipped because the scheduled work
 *   still happened and a skip would read as "nothing needed doing"; and it is
 *   reported rather than passed over silently because an operator who armed apply
 *   is otherwise looking at a green `ran` and concluding that records changed.
 *   The message names the missing variables.
 *
 * - `apply_unsupported` is a SKIP. `training_routing` is stored as `apply`, which
 *   the run-mode route refuses to write, so the row was hand-edited. Downgrading
 *   it would run the automation and hide a corrupt row behind a normal-looking
 *   result; the tick declines and says so until somebody puts it back.
 *
 * The rest are skips for the reasons their names give. `automation_disabled` and
 * `schedule_disabled` stay separate because they send an operator to different
 * switches, and one shared refusal would send them to the wrong one.
 */
export const allocationAutomationScheduledOutcomeCodeSchema = z.enum([
    'automation_disabled',
    'schedule_disabled',
    'run_in_progress',
    'unsupported',
    'apply_unsupported',
    'applied_downgraded_to_preview',
    'budget_exhausted',
    'not_recorded',
    'executor_failed',
]);
export const allocationAutomationScheduledOutcomeSchema = z.object({
    automationKey: allocationAutomationKeySchema,
    status: allocationAutomationScheduledOutcomeStatusSchema,
    /**
     * The run this produced, when one was recorded.
     *
     * Present for `ran` AND for `failed` — a failed run still has a row, and that
     * row is where the error and the partial item list live. `null` for every
     * `skipped` outcome, because nothing was started.
     */
    runId: z.string().uuid().nullable(),
    /**
     * `null` for an ordinary `ran`.
     *
     * There is exactly ONE code that comes with `status: 'ran'` —
     * `applied_downgraded_to_preview` — and it exists because a run that happened
     * but did not write is neither a success an operator can read at face value nor
     * a skip. Everything else that carries a code was skipped or failed.
     */
    code: allocationAutomationScheduledOutcomeCodeSchema.nullable(),
    /** The reason or the error, in an operator's words. `null` for an ordinary run. */
    message: z.string().nullable(),
}).strict();
/**
 * One tick of the hourly dispatcher.
 *
 * `automations` carries EVERY REGISTERED AUTOMATION every time, including those
 * that were never armed. A response listing only what ran cannot answer "why did nothing
 * happen", which is the question somebody reading this at 3am actually has, and
 * it would make "not armed" and "armed but skipped" look identical.
 */
export const allocationAutomationScheduledDispatchResponseSchema = z.object({
    dispatchedAt: z.string(),
    ran: z.number().int().min(0),
    skipped: z.number().int().min(0),
    failed: z.number().int().min(0),
    /**
     * True when at least one armed automation was left unstarted by the clock.
     *
     * Derivable from the codes below, and surfaced anyway: it is the one thing a
     * monitor should alert on, and asking a monitor to scan an array for a string
     * is how an alert quietly stops matching.
     */
    budgetExhausted: z.boolean(),
    automations: z.array(allocationAutomationScheduledOutcomeSchema),
}).strict();
//# sourceMappingURL=allocation-automation-control.js.map